<template>
    <banner-alert
        :type="bannerType"
        :closable="false">
        <template #icon>
            <pendo-progress-circle
                :colors="trialProgressColors"
                :width="33"
                :value="trialRemainingPercentage"
                :thickness="3">
                <template #default>
                    <span class="circle-content">{{ daysRemainingInTrial }}</span>
                </template>
            </pendo-progress-circle>
        </template>
        <div class="banner-copy">
            <span class="is-bold">{{ bannerTitle }}</span> {{ trialBannerMessage }}
        </div>
        <template #actions>
            <pendo-button
                v-if="!isInAutoProvisionedTrial"
                :pendo-analytics="`global-banner:${GLOBAL_BANNER_TYPES.TRIAL}:Contact sales`"
                label="Contact sales"
                size="mini"
                type="primary"
                data-cy="contact-sales-button"
                @click="$emit('primaryAction')" />
            <pendo-button
                v-if="!hasInstalled"
                :pendo-analytics="`global-banner:${GLOBAL_BANNER_TYPES.TRIAL}:Install Now`"
                label="Install Now"
                size="mini"
                type="secondary"
                data-cy="Install Now:button"
                @click="$emit('secondaryAction')" />
        </template>
    </banner-alert>
</template>

<script>
import BannerAlert from '@/common/banner-notification/banner-alert/banner-alert.vue';
import { PendoProgressCircle, PendoButton } from '@pendo/components';
import GLOBAL_BANNER_TYPES from '@/constants/global-banner-types';

const DEFAULT_DAYS_IN_TRIAL = 14;

export default {
    name: 'GlobalBanner',
    components: {
        BannerAlert,
        PendoProgressCircle,
        PendoButton,
    },
    props: {
        daysRemainingInTrial: {
            type: Number,
            required: true,
        },
        isInAutoProvisionedTrial: {
            type: Boolean,
            default: false,
        },
        hasInstalled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['primaryAction', 'secondaryAction'],
    data () {
        return {
            GLOBAL_BANNER_TYPES,
        };
    },
    computed: {
        bannerType () {
            if (!this.isTrialAlmostOver) {
                return 'info';
            }
            if (this.isTrialAlmostOver) {
                return 'warning';
            }

            return '';
        },
        bannerTitle () {
            let span = 'days';
            const autoProvisionedTrialText = `Trial will expire in ${this.daysRemainingInTrial} days.`;
            if (this.daysRemainingInTrial === 1) {
                span = 'day';
                if (this.isInAutoProvisionedTrial) {
                    return 'Today is the final day of your Pendo trial. Contact your Pendo sales representative to get Pendo for your organization!';
                }
            }

            if (this.isTrialAlmostOver) {
                return this.isInAutoProvisionedTrial
                    ? autoProvisionedTrialText
                    : `Only ${this.daysRemainingInTrial} ${span} remaining.`;
            }

            return this.isInAutoProvisionedTrial
                ? autoProvisionedTrialText
                : `${this.daysRemainingInTrial} days remaining in your trial.`;
        },
        trialProgressColors () {
            // progress circle colors are in reverse, since the circle is losing days
            return {
                center: {
                    fill: this.isTrialAlmostOver ? 'var(--palette-yellow-20)' : 'var(--palette-blue-5)',
                    stroke: this.isTrialAlmostOver ? 'var(--palette-yellow-20)' : 'var(--palette-blue-5)',
                },
                default: {
                    fill: this.isTrialAlmostOver ? 'var(--palette-orange-30)' : 'var(--palette-blue-20)',
                    stroke: this.isTrialAlmostOver ? 'var(--palette-orange-30)' : 'var(--palette-blue-20)',
                },
                progress: {
                    fill: this.isTrialAlmostOver ? 'var(--palette-yellow-100)' : 'var(--palette-teal-70)',
                    stroke: this.isTrialAlmostOver ? 'var(--palette-yellow-100)' : 'var(--palette-teal-70)',
                },
                complete: {
                    fill: 'var(--palette-teal-70)',
                    stroke: 'var(--palette-teal-70)',
                },
            };
        },
        trialBannerMessage () {
            if (this.isTrialAlmostOver && !this.isInAutoProvisionedTrial) {
                return "Don't lose your upgraded features!";
            }

            return '';
        },
        trialRemainingPercentage () {
            return (this.daysRemainingInTrial / DEFAULT_DAYS_IN_TRIAL) * 100;
        },
        isTrialAlmostOver () {
            return this.daysRemainingInTrial <= 3;
        },
    },
};
</script>

<style lang="less" scoped>
.pendo-banner {
    .pendo-alert {
        border: none;
        border-radius: 0;
    }
    .is-bold {
        font-weight: bold;
    }
}
.pendo-progress-circle {
    margin-right: 16px;
}
</style>
